import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../../API/axiosNeatex';
import type { APIResponse } from '../../../Shared/shared';

export type AllowedDiffMode = 1 | 2 | 3;

export interface PosSettlementTypeModel {
  id: string;
  name: string;
  name2: string;
  accountId: string | null;
  allowedDiffMode: AllowedDiffMode;
  isActive: boolean;
  chargeToEmployee: boolean;
}

// Add لا يحتوي id
export type AddPosSettlementTypeRequest = Omit<PosSettlementTypeModel, 'id'>;

// Update يحتوي id
export type UpdatePosSettlementTypeRequest = PosSettlementTypeModel;

// SetActive schema واضح
export type SetPosSettlementTypeActiveRequest = {
  id: string;
  isActive: boolean;
};

export type PosSettlementTypesQuery = {
  ActiveOnly?: boolean; // default true
  DiffMode?: AllowedDiffMode; // optional
};

const extractError = (error: any) =>
  error?.response?.data?.message || error?.message || 'An unexpected error occurred.';

// -------------------------
// Thunks
// -------------------------
export const addPosSettlementType = createAsyncThunk(
  'PosSettlementType/addPosSettlementType',
  async (item: AddPosSettlementTypeRequest, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<string>>('Pos/AddPosSettlementType', item);

      if (data.succeeded) return data;
      return rejectWithValue(data.message || 'Failed to add settlement type.');
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  }
);

export const updatePosSettlementType = createAsyncThunk(
  'PosSettlementType/updatePosSettlementType',
  async (item: UpdatePosSettlementTypeRequest, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<PosSettlementTypeModel>>('Pos/UpdatePosSettlementType', item);

      if (data.succeeded) return data;
      return rejectWithValue(data.message || 'Failed to update settlement type.');
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  }
);

export const getPosSettlementTypeById = createAsyncThunk(
  'PosSettlementType/getPosSettlementTypeById',
  async (Id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get<APIResponse<PosSettlementTypeModel>>(
        `Pos/GetPosSettlementTypeById?Id=${encodeURIComponent(Id)}`
      );

      if (data.succeeded) return data.data;
      return rejectWithValue(data.message || 'Failed to load settlement type.');
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  }
);

export const setPosSettlementTypeActive = createAsyncThunk(
  'PosSettlementType/setPosSettlementTypeActive',
  async (payload: SetPosSettlementTypeActiveRequest, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post<APIResponse<boolean>>('Pos/SetPosSettlementTypeActive', payload);

      if (data.succeeded) return { payload, response: data };
      return rejectWithValue(data.message || 'Failed to change active status.');
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  }
);

export const getPosSettlementTypesQuery = createAsyncThunk(
  'PosSettlementType/getPosSettlementTypesQuery',
  async (query: PosSettlementTypesQuery | undefined, { rejectWithValue }) => {
    try {
      const params: PosSettlementTypesQuery = {
        ActiveOnly: query?.ActiveOnly ?? true,
        ...(query?.DiffMode ? { DiffMode: query.DiffMode } : {})
      };

      const { data } = await ntxAPI.get<APIResponse<PosSettlementTypeModel[]>>('Pos/GetPosSettlementTypesQuery', {
        params
      });

      if (data.succeeded) return data.data;
      return rejectWithValue(data.message || 'Failed to load settlement types.');
    } catch (error: any) {
      return rejectWithValue(extractError(error));
    }
  }
);

// -------------------------
// Slice
// -------------------------
type State = {
  settlementTypes: PosSettlementTypeModel[];
  settlementTypeById: PosSettlementTypeModel | null;

  loading: boolean;
  error: string | null;

  lastActionOk: boolean | null;
  lastMessage: string | null;

  // إذا كان Add يرجع string = id (غالباً)
  lastCreatedId: string | null;
};

const initialState: State = {
  settlementTypes: [],
  settlementTypeById: null,

  loading: false,
  error: null,

  lastActionOk: null,
  lastMessage: null,

  lastCreatedId: null
};

const PosSettlementTypeSlice = createSlice({
  name: 'PosSettlementType',
  initialState,
  reducers: {
    clearPosSettlementTypeState: state => {
      state.error = null;
      state.lastActionOk = null;
      state.lastMessage = null;
      state.lastCreatedId = null;
    },
    clearSettlementTypeById: state => {
      state.settlementTypeById = null;
    }
  },
  extraReducers: builder => {
    const pending = (state: State) => {
      state.loading = true;
      state.error = null;
      state.lastActionOk = null;
      state.lastMessage = null;
      state.lastCreatedId = null;
    };

    const rejected = (state: State, action: PayloadAction<any>) => {
      state.loading = false;
      state.error = action.payload ?? 'Request failed.';
      state.lastActionOk = false;
    };

    builder
      // ----------------
      // List (Query)
      // ----------------
      .addCase(getPosSettlementTypesQuery.pending, pending)
      .addCase(getPosSettlementTypesQuery.fulfilled, (state, action: PayloadAction<PosSettlementTypeModel[]>) => {
        state.loading = false;
        state.settlementTypes = action.payload ?? [];
        state.lastActionOk = true;
      })
      .addCase(getPosSettlementTypesQuery.rejected, rejected)

      // ----------------
      // By Id
      // ----------------
      .addCase(getPosSettlementTypeById.pending, pending)
      .addCase(getPosSettlementTypeById.fulfilled, (state, action: PayloadAction<PosSettlementTypeModel>) => {
        state.loading = false;
        state.settlementTypeById = action.payload;
        state.lastActionOk = true;
      })
      .addCase(getPosSettlementTypeById.rejected, rejected)

      // ----------------
      // Add
      // ----------------
      // .addCase(addPosSettlementType.pending, pending)
      // .addCase(addPosSettlementType.fulfilled, (state, action: PayloadAction<APIResponse<string>>) => {
      //   state.loading = false;
      //   state.lastActionOk = true;
      //   state.lastMessage = action.payload?.message ?? null;

      //   // غالباً يرجع Id كسلسلة
      //   state.lastCreatedId = action.payload?.data ?? null;
      // })
      // .addCase(addPosSettlementType.rejected, rejected)

      // ----------------
      // Update
      // ----------------
      .addCase(updatePosSettlementType.pending, pending)
      .addCase(
        updatePosSettlementType.fulfilled,
        (state, action: PayloadAction<APIResponse<PosSettlementTypeModel>>) => {
          state.loading = false;
          state.lastActionOk = true;
          state.lastMessage = action.payload?.message ?? null;

          const updated = action.payload?.data;
          if (!updated?.id) return;

          const idx = state.settlementTypes.findIndex(x => x.id === updated.id);
          if (idx >= 0) state.settlementTypes[idx] = { ...state.settlementTypes[idx], ...updated };

          if (state.settlementTypeById?.id === updated.id) {
            state.settlementTypeById = { ...state.settlementTypeById, ...updated };
          }
        }
      )
      .addCase(updatePosSettlementType.rejected, rejected)

      // ----------------
      // Set Active
      // ----------------
      .addCase(setPosSettlementTypeActive.pending, pending)
      .addCase(
        setPosSettlementTypeActive.fulfilled,
        (
          state,
          action: PayloadAction<{ payload: SetPosSettlementTypeActiveRequest; response: APIResponse<boolean> }>
        ) => {
          state.loading = false;
          state.lastActionOk = true;
          state.lastMessage = action.payload?.response?.message ?? null;

          const { id, isActive } = action.payload.payload;

          const idx = state.settlementTypes.findIndex(x => x.id === id);
          if (idx >= 0) state.settlementTypes[idx] = { ...state.settlementTypes[idx], isActive };

          if (state.settlementTypeById?.id === id) {
            state.settlementTypeById = { ...state.settlementTypeById, isActive };
          }
        }
      )
      .addCase(setPosSettlementTypeActive.rejected, rejected);
  }
});

export const { clearPosSettlementTypeState , clearSettlementTypeById} = PosSettlementTypeSlice.actions;
export default PosSettlementTypeSlice.reducer;

////
// @store/POS/PosSettlementType/posSettlementType.selectors.ts
export const selectPosSettlementTypeState = (state: any) => state.PosSettlementType;

export const selectSettlementTypes = (state: any) => selectPosSettlementTypeState(state).settlementTypes;
export const selectSettlementTypeById = (state: any) => selectPosSettlementTypeState(state).settlementTypeById;

export const selectPosSettlementTypeLoading = (state: any) => selectPosSettlementTypeState(state).loading;
export const selectPosSettlementTypeError = (state: any) => selectPosSettlementTypeState(state).error;

export const selectPosSettlementTypeLastMessage = (state: any) => selectPosSettlementTypeState(state).lastMessage;
export const selectPosSettlementTypeLastActionOk = (state: any) => selectPosSettlementTypeState(state).lastActionOk;
